import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { Skeleton, Text } from '@fhs/ui';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { isDelivery } from '@fhs-legacy/frontend/src/utils/service-mode';
import { CHECKOUT_ORDER_SUMMARY_TOTAL } from '@fhs-legacy/frontend/src/utils/test-ids';

import { useTipStore } from '../../../state/tip-store';
import { OriginalPriceDisplay } from '../../cart-components/item/item-profile/item-price/item-price-components';

export default function TotalRow() {
  const { serviceMode } = useServiceModeContext();
  const { serverOrder, serverOrderLoading } = useCartContext();
  const selectedTip = useTipStore(state => state.selectedTip);
  const otherTipValue = useTipStore(state => state.otherTipValue);
  const calculateTipCents = useTipStore(state => state.calculateTipCents);
  const enableZeroPricedOrder = useFlag('enable-zero-cart-total-order');

  const discountAmount = useMemo(() => {
    const discounts = serverOrder?.cart?.discounts || [];
    return discounts.reduce((sum, d) => sum + d.value, 0);
  }, [serverOrder?.cart?.discounts]);

  const tipCents = useMemo(() => {
    if (!isDelivery(serviceMode)) {
      return 0;
    }
    const subTotalCents = serverOrder?.cart?.subTotalCents ?? 0;
    return calculateTipCents(subTotalCents, discountAmount);

    // requires selectedTip and otherTipValue to update, leave in dep array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    serviceMode,
    serverOrder?.cart?.subTotalCents,
    discountAmount,
    calculateTipCents,
    selectedTip,
    otherTipValue,
  ]);

  const totalCents = useMemo(() => {
    const baseTotal = serverOrder?.cart?.totalCents || 0;
    return baseTotal + tipCents;
  }, [serverOrder?.cart?.totalCents, tipCents]);

  const hasDiscount = discountAmount > 0;
  const taxCents = serverOrder?.cart?.taxCents || 0;
  if (serverOrderLoading || !serverOrder?.cart || (!enableZeroPricedOrder && taxCents === 0)) {
    return (
      <View testID={CHECKOUT_ORDER_SUMMARY_TOTAL} style={styles.container}>
        <Text.Heading type="four">Total</Text.Heading>
        <Skeleton style={styles.skeleton} />
      </View>
    );
  }

  const formattedTotal = (totalCents / 100).toFixed(2);

  return (
    <View
      testID={CHECKOUT_ORDER_SUMMARY_TOTAL}
      style={styles.container}
      accessibilityRole="text"
      accessibilityLabel={`Total: ${formattedTotal}${hasDiscount ? '. Discount applied' : ''}`}
    >
      <Text.Heading type="four">Total</Text.Heading>
      <View style={styles.priceContainer}>
        {hasDiscount && (
          <OriginalPriceDisplay
            subtotalID
            price={totalCents + discountAmount}
            shouldShowStrikeThrough
          />
        )}
        <OriginalPriceDisplay subtotalID price={totalCents} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 16,
    marginHorizontal: 28,
  },

  priceContainer: {
    flexDirection: 'row',
    gap: 9,
    alignItems: 'center',
  },

  skeleton: {
    height: 16,
    width: 48,
    borderRadius: 4,
  },
});
